import {
  Tab,
  TabList,
  TabListProps,
  TabPanel,
  TabPanelProps,
  TabPanels,
  TabPanelsProps,
  TabProps,
  Tabs,
  TabsProps,
} from "@chakra-ui/react";
import React, { isValidElement, useCallback } from "react";

type Single<T> = T extends Array<infer E> ? E : never;

export interface ComponentTabsProps extends Omit<TabsProps, "children"> {
  tabList: ComponentTabItem[];
}

export interface ComponentTabsExtraProps {
  tabPanelsProps?: TabPanelsProps;
  tabListProps?: TabListProps;
  tabPanelProps?: TabPanelProps;
  tabListItemProps?: TabProps;
}

export interface ComponentTabItem {
  title: string;
  component: React.ElementType | React.ReactNode;
  componentProps?: Record<string, unknown>;
  headProps?: TabProps;
  bodyProps?: TabPanelProps;
}

function ComponentTabs({
  tabList,
  tabListProps,
  tabPanelsProps,
  tabPanelProps,
  tabListItemProps,
  ...props
}: ComponentTabsProps & ComponentTabsExtraProps) {
  const getValidComponent = useCallback(
    (component: Single<typeof tabList>["component"]) => {
      return isValidElement(component)
        ? component
        : (component as React.ElementType);
    },
    [],
  );

  if (tabList.length === 1) {
    const { component } = tabList[0];
    return <>{getValidComponent(component)}</>;
  }

  return (
    <Tabs flex={1} {...props}>
      <TabList {...tabListProps} mb="4">
        {tabList.map((tItem, index) => (
          <Tab
            key={`tab-${tItem.title}-${index}`}
            fontSize={{ base: "sm", md: "md" }}
            px={{ base: 2, md: 4 }}
            justifyContent={{ base: "flex-start", md: "center" }}
            w={{ base: "full", md: "auto" }}
            {...tItem.headProps}
            {...tabListItemProps}
            _selected={{
              textColor: "primary.500",
              borderColor: "primary.500",
              ...tabListItemProps?._selected,
            }}
          >
            {tItem.title}
          </Tab>
        ))}
      </TabList>
      <TabPanels h="full" {...tabPanelsProps}>
        {tabList.map((tItem, index) => {
          const Component = getValidComponent(tItem.component);
          return (
            <TabPanel
              flex={1}
              {...tabPanelProps}
              {...tItem.bodyProps}
              key={`tabPanel-${tItem.title}-${index}`}
            >
              {isValidElement(Component) ? (
                Component
              ) : (
                <Component {...tItem.componentProps} />
              )}
            </TabPanel>
          );
        })}
      </TabPanels>
    </Tabs>
  );
}

export default ComponentTabs;
